import { uploadData, getUrl, downloadData, remove, list } from '@aws-amplify/storage';
import { IStorageService } from '../../../services/storage/StorageService';
import { UploadOptions, UploadResult, StorageItem } from '../../../../shared/types/storage';
import { AWSConfig } from '../../../config/BackendConfig';

/**
 * AWS S3 implementation of the storage service using AWS Amplify Storage
 */
export class S3StorageService implements IStorageService {
  private config: AWSConfig['storage'];
  private tenantId: string;

  constructor(config: AWSConfig['storage'], tenantId: string) {
    this.config = config;
    this.tenantId = tenantId;
  }

  async upload(file: File, path: string, options?: UploadOptions): Promise<UploadResult> {
    try {
      const tenantId = options?.tenantId;
      if (!tenantId) {
        throw new Error('No tenant ID provided for file upload');
      }

      const fullPath = `${tenantId}/${path}`;
      
      const uploadTask = uploadData({
        path: fullPath,
        data: file,
        options: {
          contentType: options?.contentType || file.type,
          metadata: options?.metadata,
        },
      });

      const result = await uploadTask.result;
      
      // Get the signed URL for the uploaded file
      const urlResult = await getUrl({
        path: fullPath,
        options: {
          expiresIn: 900, // 15 minutes
        },
      });

      return {
        path: fullPath,
        url: urlResult.url.toString(),
        size: file.size,
        etag: '', // S3 doesn't provide etag in upload result
      };
    } catch (error) {
      console.error('S3 upload error:', error);
      throw error;
    }
  }

  async download(path: string): Promise<Blob> {
    try {
      const downloadResult = await downloadData({
        path,
      });

      const result = await downloadResult.result;
      // Convert the response body to a Blob
      if (result.body instanceof Blob) {
        return result.body;
      } else {
        // Handle other response body types
        const arrayBuffer = await (result.body as any).arrayBuffer();
        return new Blob([arrayBuffer]);
      }
    } catch (error) {
      console.error('S3 download error:', error);
      throw error;
    }
  }

  async getSignedUrl(path: string, expiresIn: number = 900): Promise<string> {
    try {
      // If path doesn't already include tenant ID, add it
      const fullPath = path.startsWith(`${this.tenantId}/`) ? path : `${this.tenantId}/${path}`;
      
      const urlResult = await getUrl({
        path: fullPath,
        options: {
          expiresIn,
        },
      });

      return urlResult.url.toString();
    } catch (error) {
      console.error('S3 signed URL error:', error);
      throw error;
    }
  }

  async delete(path: string, tenantId?: string): Promise<void> {
    try {
      if (!tenantId) {
        throw new Error('No tenant ID provided for file deletion');
      }

      // If path doesn't already include tenant ID, add it
      const fullPath = path.startsWith(`${tenantId}/`) ? path : `${tenantId}/${path}`;
      
      await remove({
        path: fullPath,
      });
    } catch (error) {
      console.error('S3 delete error:', error);
      throw error;
    }
  }

  async list(prefix: string, tenantId?: string): Promise<StorageItem[]> {
    try {
      if (!tenantId) {
        throw new Error('No tenant ID provided for file listing');
      }

      // If prefix doesn't already include tenant ID, add it
      const fullPrefix = prefix.startsWith(`${tenantId}/`) ? prefix : `${tenantId}/${prefix}`;
      
      const listResult = await list({
        path: fullPrefix,
      });

      return listResult.items.map(item => ({
        path: item.path,
        size: item.size || 0,
        lastModified: item.lastModified || new Date(),
        etag: item.eTag || '',
      }));
    } catch (error) {
      console.error('S3 list error:', error);
      throw error;
    }
  }
}